import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { siteMetadata } from "../../site-metadata";
import { useLocation } from '@reach/router';

const SEO = ({ seo = { titleNoTrail: false } }) => {
  const location = useLocation();
  const { strapiGlobal } = useStaticQuery(query);
  const { defaultSeo, siteName, favicon } = strapiGlobal;

  // Merge default and page-specific SEO values
  const fullSeo = { ...defaultSeo, ...seo };

  // Compute shareImage URL (handle string or object with localFile.publicURL)
  const getShareImageUrl = () => {
    if (!fullSeo.shareImage) return null;
    if (typeof fullSeo.shareImage === 'string') return fullSeo.shareImage;
    if (fullSeo.shareImage.localFile?.publicURL) {
      return (process.env.GATSBY_ROOT_URL || "http://localhost:8000") + fullSeo.shareImage.localFile.publicURL;
    }
    if (fullSeo.shareImage.publicURL) {
      return (process.env.GATSBY_ROOT_URL || "http://localhost:8000") + fullSeo.shareImage.publicURL;
    }
    return null;
  };

  const shareImageUrl = getShareImageUrl();
  const truncatedDescription = fullSeo.metaDescription && fullSeo.metaDescription.length > 155
    ? (fullSeo.metaDescription || "").slice(0, 152) + '...'
    : fullSeo.metaDescription;

  const noTrail = fullSeo.metaTitle.split('|').length > 1
    || (`${fullSeo.metaTitle} | ${siteName}`.length > 65)
    || seo.titleNoTrail === true;

  const canonical = seo.canonical || (location && location.pathname ? siteMetadata.siteUrl + location.pathname : siteMetadata.siteUrl);

  return (
    <Helmet
      htmlAttributes={{ lang: 'es' }}
      title={fullSeo.metaTitle.length > 65 && fullSeo.metaTitle.split('|').length > 1 ? fullSeo.metaTitle.split('|')[0] : fullSeo.metaTitle}
      titleTemplate={noTrail ? `%s` : `%s | ${siteName}`}
    >
      <link rel="icon" href={favicon?.localFile?.publicURL} />
      <link rel="canonical" href={canonical} />
      {fullSeo.noindex && <meta name="robots" content="noindex, follow" />}
      <meta name="theme-color" content="#174aa5" />
      {fullSeo.metaTitle && <meta property="og:title" content={fullSeo.metaTitle} />}
      {fullSeo.metaTitle && <meta name="twitter:title" content={fullSeo.metaTitle} />}
      {fullSeo.metaDescription && <meta name="description" content={truncatedDescription} />}
      {fullSeo.metaDescription && <meta property="og:description" content={fullSeo.metaDescription} />}
      {fullSeo.metaDescription && <meta name="twitter:description" content={fullSeo.metaDescription} />}
      {shareImageUrl && <meta name="image" content={shareImageUrl} />}
      {shareImageUrl && <meta property="og:image" content={shareImageUrl} />}
      {shareImageUrl && <meta name="twitter:image" content={shareImageUrl} />}
      {fullSeo.article && <meta property="og:type" content="article" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="og:locale" content="es_ES" />
      <meta name="language" content="es" />
      {fullSeo && fullSeo.scripts && [
        ...fullSeo.scripts,
        // Note d'avis du magasin : pas sur les articles de blog (Google n'accepte pas
        // d'avis auto-déclarés sur ce type de page).
        ...(fullSeo.article ? [] : [{
          type: "application/ld+json",
          content: `{
             "@context": "https://schema.org",
              "@type": "Store",
              "name": "El Taller del Tote Bag",
              "url": "https://tallerdeltotebag.es/",
              "image": "https://tallerdeltotebag.es/static/e528c1c1c237747e3250b318ce0bc0b1/ca0ed/Logo-latelierdutotebag.webp",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "bestRating": "5",
                "ratingCount": "550"
              }
          }`
        }]),
      ].map((script, i) => (
        <script key={i} type={script.type}>
          {script.content}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;



const query = graphql`
  query {
    strapiGlobal {
      siteName
      favicon {
        localFile {
          publicURL
        }
      }
      defaultSeo {
        metaTitle
        metaDescription
        shareImage {
          localFile {
            publicURL
          }
        }
      }
    }
  }
`;
